import React, { useState, useEffect, useRef } from "react";
import { useTheme } from "@mui/material/styles";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import TablePagination from "@mui/material/TablePagination";
import Box from "@mui/material/Box";
import IconButton from "@mui/material/IconButton";
import DownloadIcon from "@mui/icons-material/Download";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import Popper from "@mui/material/Popper";
import Paper from "@mui/material/Paper";
import Typography from "@mui/material/Typography";
import Fade from "@mui/material/Fade";
import * as u from "./utility";
import { useNewTabText } from "./hooks";
// @ts-ignore - papaparse module resolution issue
const Papa = require("papaparse");

type CsvPreviewProps = {
  data: string;
  fontSize?: number;
  maxRows?: number; // Max rows to show at once (undefined = show all)
};

export function CsvPreview({ data, fontSize, maxRows }: CsvPreviewProps) {
  const theme = useTheme();

  // Parse CSV synchronously so it works with renderToString (for new tab)
  const parseResult = React.useMemo(() => {
    try {
      const result = Papa.parse(data, {
        header: true,
        skipEmptyLines: true,
      });

      if (result.error) {
        return { error: result.error.message, headers: [], rows: [] };
      }

      if (result.data.length > 0) {
        const firstRow = result.data[0] as any;
        // Filter out PapaParse internal fields
        const headers = Object.keys(firstRow).filter(
          (key) => !key.startsWith("__parsed")
        );
        return {
          headers,
          rows: result.data,
          error: undefined,
        };
      } else {
        return { error: "No data found in CSV", headers: [], rows: [] };
      }
    } catch (err: any) {
      return { error: err.message, headers: [], rows: [] };
    }
  }, [data]);

  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(maxRows || 100);
  const [containerWidth, setContainerWidth] = useState<number | undefined>();
  const outerBoxRef = useRef<HTMLDivElement>(null);
  const tableContainerRef = useRef<HTMLDivElement>(null);
  const tableRef = useRef<HTMLTableElement>(null);

  const { headers, rows, error } = parseResult;

  // Detect if we're in a new tab context (renderToString) - refs don't work there
  const [isNewTab, setIsNewTab] = useState(true);

  useEffect(() => {
    // If this effect runs, we're in a browser, not renderToString
    setIsNewTab(false);
  }, []);

  useEffect(() => {
    if (!outerBoxRef.current) {
      // console.log("No outerBoxRef");
      return;
    }

    const el: HTMLElement | null = u.findMessagePreviewContainer(
      outerBoxRef.current
    );
    if (!el) {
      // console.log("Could not find MessagePreview container");
      return;
    }

    const initialWidth = el.getBoundingClientRect().width * 0.9;
    // console.log(`Setting initial container width: ${initialWidth}`);
    setContainerWidth(initialWidth);

    const observer = new ResizeObserver((entries: ResizeObserverEntry[]) => {
      const width = Math.floor(
        entries[0].target.getBoundingClientRect().width * 0.9
      );
      // console.log(`Container width changed: ${width}`);
      setContainerWidth(width);
    });
    observer.observe(el);

    return () => {
      observer.disconnect();
    };
  }, []);

  // In new tab (SSR context) or if parsing failed, show raw CSV text
  if (isNewTab || error || headers.length === 0) {
    return <>{data}</>;
  }

  // If maxRows is undefined, show all rows; otherwise use pagination
  const shouldPaginate = maxRows !== undefined;
  const visibleRows = shouldPaginate
    ? rows.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
    : rows;

  return (
    <Box
      ref={outerBoxRef}
      sx={{
        width: "100%",
        maxWidth: "100%",
        minWidth: 0,
        overflow: "hidden",
        contain: "layout",
      }}
    >
      <TableContainer
        ref={tableContainerRef}
        sx={{
          width: containerWidth ? `${containerWidth}px` : "auto",
          maxWidth: containerWidth ? `${containerWidth}px` : "none",
          overflowX: "auto",
          overflowY: "auto",
          // Custom scrollbar styling
          "&::-webkit-scrollbar": {
            width: "6px",
            height: "6px",
          },
          "&::-webkit-scrollbar-track": {
            background: "transparent",
          },
          "&::-webkit-scrollbar-thumb": {
            background:
              theme.palette.mode === "dark"
                ? "rgba(232, 234, 237, 0.12)"
                : "rgba(95, 99, 104, 0.12)",
            borderRadius: "3px",
            transition: "background 0.2s ease",
          },
          "&::-webkit-scrollbar-thumb:hover": {
            background:
              theme.palette.mode === "dark"
                ? "rgba(232, 234, 237, 0.24)"
                : "rgba(95, 99, 104, 0.24)",
          },
          // Firefox scrollbar styling
          scrollbarWidth: "thin",
          scrollbarColor:
            theme.palette.mode === "dark"
              ? "rgba(232, 234, 237, 0.12) transparent"
              : "rgba(95, 99, 104, 0.12) transparent",
        }}
      >
        <Table
          ref={tableRef}
          size="small"
          sx={{
            width: "auto",
          }}
        >
          <TableHead>
            <TableRow
              sx={{
                borderBottom:
                  theme.palette.mode === "dark"
                    ? "1px solid rgba(255, 255, 255, 0.12)"
                    : "1px solid #e0e0e0",
              }}
            >
              {headers.map((header, index) => (
                <TableCell
                  key={index}
                  sx={{
                    fontSize: (fontSize || 14) - 2,
                    fontWeight: 500,
                    color: "var(--text-secondary)",
                    py: "2px !important",
                    px: "4px !important",
                    whiteSpace: "nowrap",
                  }}
                >
                  {header}
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {visibleRows.map((row: any, rowIndex: number) => (
              <TableRow
                key={rowIndex}
                sx={{
                  borderBottom:
                    theme.palette.mode === "dark"
                      ? "1px solid rgba(255, 255, 255, 0.06)"
                      : "1px solid #e0e0e0",
                }}
              >
                {headers.map((header, cellIndex) => {
                  const cellValue = row[header];
                  const cellStr = cellValue == null ? "" : String(cellValue);
                  const isEmpty = !cellStr || cellStr.trim() === "";
                  return (
                    <TableCell
                      key={cellIndex}
                      sx={{
                        fontSize: (fontSize || 14) - 2,
                        fontFamily: "monospace",
                        color: "var(--text-primary)",
                        py: "2px !important",
                        px: "4px !important",
                        whiteSpace: "nowrap",
                        maxWidth: isEmpty ? "50px" : "400px",
                        minWidth: isEmpty ? "30px" : undefined,
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                      }}
                    >
                      {cellStr}
                    </TableCell>
                  );
                })}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
      {shouldPaginate && rows.length > rowsPerPage && (
        <Box
          sx={{
            display: "flex",
            justifyContent: "flex-start",
            mt: 1,
          }}
        >
          <TablePagination
            component="div"
            count={rows.length}
            page={page}
            onPageChange={(_, newPage) => setPage(newPage)}
            rowsPerPage={rowsPerPage}
            onRowsPerPageChange={(e) => {
              setRowsPerPage(parseInt(e.target.value, 10));
              setPage(0);
            }}
            rowsPerPageOptions={[50, 100, 250, 500]}
            labelRowsPerPage=""
            sx={{
              color: "var(--text-secondary)",
              "& .MuiTablePagination-toolbar": {
                minHeight: "40px",
                paddingLeft: 0,
              },
              "& .MuiTablePagination-spacer": {
                display: "none",
              },
              "& .MuiTablePagination-selectLabel": {
                display: "none",
              },
              "& .MuiTablePagination-displayedRows": {
                fontSize: 11,
                marginLeft: 0,
                marginRight: "8px",
              },
              "& .MuiTablePagination-select": {
                fontSize: 11,
              },
              "& .MuiTablePagination-actions": {
                marginLeft: "8px",
              },
            }}
          />
        </Box>
      )}
    </Box>
  );
}

export function CsvFilePreview({
  data,
  fontSize,
  fileName,
}: {
  data: string;
  fontSize?: number;
  fileName?: string;
}) {
  const theme = useTheme();
  const [csvData, setCsvData, handleDoubleClick] = useNewTabText();
  const [hover, setHover] = useState(false);
  const [anchor, setAnchor] = useState<HTMLElement | undefined>();

  useEffect(() => {
    setCsvData(data);
  }, [data]);

  const handleDownload = () => {
    if (!csvData) return;
    const link = document.createElement("a");
    link.href = "data:text/csv," + encodeURIComponent(csvData);
    link.download = fileName || "file.csv";
    link.click();
  };

  const handleCopy = (e: React.MouseEvent<HTMLButtonElement>) => {
    if (!csvData) return;
    navigator.clipboard.writeText(csvData);
    setAnchor(e.currentTarget);
    setTimeout(() => {
      setAnchor(undefined);
    }, 1000);
  };

  if (!csvData) {
    return null;
  }

  return (
    <Box
      sx={{ position: "relative", width: "100%" }}
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
      onClick={(e) => handleDoubleClick(e)}
    >
      {hover && (
        <>
          <Box
            sx={{
              position: "absolute",
              left: 4,
              top: 4,
              zIndex: 5,
              display: "flex",
              alignItems: "center",
              gap: 0.25,
              px: 0.5,
              py: 0.25,
              borderRadius: "12px",
              background:
                theme.palette.mode === "dark"
                  ? "rgba(0, 0, 0, 0.6)"
                  : "rgba(255, 255, 255, 0.85)",
              backdropFilter: "blur(8px)",
              boxShadow: "0 1px 4px rgba(0, 0, 0, 0.15)",
            }}
          >
            <IconButton
              onClick={handleDownload}
              size="small"
              sx={{
                p: 0.25,
                color:
                  theme.palette.mode === "dark"
                    ? "rgba(255, 255, 255, 0.9)"
                    : "rgba(0, 0, 0, 0.7)",
                "&:hover": {
                  background:
                    theme.palette.mode === "dark"
                      ? "rgba(255, 255, 255, 0.1)"
                      : "rgba(0, 0, 0, 0.05)",
                },
              }}
            >
              <DownloadIcon sx={{ fontSize: 16 }} />
            </IconButton>
            <IconButton
              onClick={handleCopy}
              size="small"
              sx={{
                p: 0.25,
                color:
                  theme.palette.mode === "dark"
                    ? "rgba(255, 255, 255, 0.9)"
                    : "rgba(0, 0, 0, 0.7)",
                "&:hover": {
                  background:
                    theme.palette.mode === "dark"
                      ? "rgba(255, 255, 255, 0.1)"
                      : "rgba(0, 0, 0, 0.05)",
                },
              }}
            >
              <ContentCopyIcon sx={{ fontSize: 16 }} />
            </IconButton>
          </Box>
          <Popper
            open={anchor != null}
            anchorEl={anchor}
            placement="right-start"
            transition
          >
            {({ TransitionProps }) => (
              <Fade {...TransitionProps} timeout={350}>
                <Paper>
                  <Typography fontSize={10} sx={{ p: 1 }}>
                    copied!
                  </Typography>
                </Paper>
              </Fade>
            )}
          </Popper>
        </>
      )}
      <CsvPreview data={csvData} fontSize={fontSize} maxRows={50} />
    </Box>
  );
}
